import React, { CSSProperties, MouseEventHandler } from "react";
import { acaPal, acaFont } from "./tokens";

/* ─── AcaLabel — uppercase mono caption ─────────────────────────── */
export const AcaLabel: React.FC<{ children: React.ReactNode; color?: string; style?: CSSProperties }> = ({
  children,
  color,
  style,
}) => (
  <div
    style={{
      fontFamily: acaFont.mono,
      fontSize: 10,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: color || acaPal.inkSoft,
      marginBottom: 6,
      ...style,
    }}
  >
    {children}
  </div>
);

/* ─── AcaCTA — square button with hairline connector + arrow ────── */
type CtaKind = "ink" | "accent" | "outline" | "paper";
type CtaSize = "sm" | "md" | "lg";

interface AcaCTAProps {
  children: React.ReactNode;
  kind?: CtaKind;
  size?: CtaSize;
  href?: string;
  onClick?: MouseEventHandler;
  type?: "button" | "submit";
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

export const AcaCTA: React.FC<AcaCTAProps> = ({
  children,
  kind = "ink",
  size = "md",
  href,
  onClick,
  type = "button",
  disabled,
  className,
  style,
}) => {
  const sizes = {
    sm: { padding: "11px 18px", fontSize: 10.5, gap: 10, line: 18, arrow: 13 },
    md: { padding: "14px 24px", fontSize: 11,   gap: 14, line: 24, arrow: 14 },
    lg: { padding: "17px 30px", fontSize: 12,   gap: 18, line: 30, arrow: 15 },
  } as const;
  const s = sizes[size];
  const kinds: Record<CtaKind, { bg: string; fg: string; border?: string }> = {
    ink:     { bg: acaPal.ink,    fg: acaPal.paper },
    accent:  { bg: acaPal.accent, fg: "#fff" },
    outline: { bg: "transparent", fg: acaPal.ink, border: `1px solid ${acaPal.ink}` },
    paper:   { bg: acaPal.paper,  fg: acaPal.ink,  border: `1px solid ${acaPal.ink}` },
  };
  const k = kinds[kind];
  const baseStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: s.gap,
    padding: s.padding,
    background: k.bg,
    color: k.fg,
    border: k.border || "none",
    fontFamily: acaFont.mono,
    fontSize: s.fontSize,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    textDecoration: "none",
    opacity: disabled ? 0.55 : 1,
    ...style,
  };
  const content = (
    <>
      <span>{children}</span>
      <span style={{ width: s.line, height: 1, background: k.fg }} />
      <span style={{ fontSize: s.arrow + 1 }}>→</span>
    </>
  );
  if (href && !disabled) {
    return (
      <a href={href} onClick={onClick} className={className} style={baseStyle}>
        {content}
      </a>
    );
  }
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={className} style={baseStyle}>
      {content}
    </button>
  );
};

/* ─── AcaRuleLink — inline mono-uppercase link with underline ───── */
export const AcaRuleLink: React.FC<{
  children: React.ReactNode;
  href?: string;
  onClick?: MouseEventHandler;
  color?: string;
  style?: CSSProperties;
}> = ({ children, href, onClick, color = acaPal.ink, style }) => (
  <a
    href={href}
    onClick={onClick}
    style={{
      display: "inline-flex",
      gap: 10,
      alignItems: "center",
      fontFamily: acaFont.mono,
      fontSize: 10.5,
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      color,
      cursor: "pointer",
      borderBottom: `1px solid ${color}`,
      paddingBottom: 4,
      textDecoration: "none",
      ...style,
    }}
  >
    {children}
    <span style={{ fontSize: 13 }}>→</span>
  </a>
);

/* ─── AcaSectionHead — §-number eyebrow + large title ───────────── */
export const AcaSectionHead: React.FC<{
  num?: string;
  eyebrow?: string;
  title: React.ReactNode;
  right?: React.ReactNode;
  divider?: "1px" | "2px";
  style?: CSSProperties;
}> = ({ num, eyebrow, title, right, divider = "1px", style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      borderBottom: `${divider} solid ${divider === "2px" ? acaPal.ink : acaPal.rule}`,
      paddingBottom: 14,
      flexWrap: "wrap",
      gap: 16,
      ...style,
    }}
  >
    <div>
      {(num || eyebrow) && (
        <div
          style={{
            fontFamily: acaFont.mono,
            fontSize: 10.5,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: acaPal.inkSoft,
          }}
        >
          {num && <>{num}{eyebrow ? " · " : ""}</>}
          {eyebrow}
        </div>
      )}
      <h2
        style={{
          fontFamily: acaFont.sans,
          fontSize: 44,
          margin: "6px 0 0",
          fontWeight: 600,
          letterSpacing: "-0.025em",
          lineHeight: 1,
          color: acaPal.ink,
        }}
      >
        {title}
      </h2>
    </div>
    {right && (
      <div
        style={{
          fontFamily: acaFont.mono,
          fontSize: 10.5,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: acaPal.inkSoft,
        }}
      >
        {right}
      </div>
    )}
  </div>
);

/* ─── AcaPageHero — page-level masthead (About/Blog/Contact) ────── */
export const AcaPageHero: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: string[];
}> = ({ eyebrow, title, subtitle, meta }) => (
  <section style={{ borderBottom: `1px solid ${acaPal.rule}`, background: acaPal.paper }}>
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 20px 56px" }}>
      {meta && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 16,
            borderBottom: `1px solid ${acaPal.rule}`,
            paddingBottom: 14,
            fontFamily: acaFont.mono,
            fontSize: 10.5,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: acaPal.inkSoft,
            marginBottom: 32,
          }}
        >
          {meta.map((m, i) => (
            <div
              key={i}
              style={{ gridColumn: "span 4", textAlign: i === 2 ? "right" : "left" }}
            >
              {m}
            </div>
          ))}
        </div>
      )}
      {eyebrow && (
        <div
          style={{
            fontFamily: acaFont.mono,
            fontSize: 11,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: acaPal.accent,
            marginBottom: 22,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ width: 28, height: 1, background: acaPal.accent }} />
          {eyebrow}
        </div>
      )}
      <h1
        style={{
          fontFamily: acaFont.sans,
          fontSize: "clamp(56px, 9vw, 112px)",
          lineHeight: 0.92,
          letterSpacing: "-0.04em",
          fontWeight: 600,
          margin: 0,
          color: acaPal.ink,
          maxWidth: 1100,
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          style={{
            fontFamily: acaFont.serif,
            fontSize: 20,
            lineHeight: 1.5,
            color: acaPal.inkSoft,
            maxWidth: 760,
            margin: "32px 0 0",
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  </section>
);

/* ─── AcaIcon — minimal hairline SVG set ────────────────────────── */
type IconName =
  | "search" | "arrow" | "arrowUp" | "pin" | "clock" | "doc"
  | "dot" | "close" | "plus" | "check" | "star" | "book"
  | "globe" | "rss" | "filter" | "menu" | "bookmark" | "mail" | "phone";

export const AcaIcon: React.FC<{ name: IconName; size?: number; stroke?: string }> = ({
  name,
  size = 16,
  stroke = "currentColor",
}) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke,
    strokeWidth: 1.4,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const paths: Record<IconName, React.ReactNode> = {
    search:   <><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4-4" /></>,
    arrow:    <path d="M5 12h14m-6-6 6 6-6 6" />,
    arrowUp:  <path d="M7 17 17 7m0 0H9m8 0v8" />,
    pin:      <><path d="M12 21s-7-7.5-7-12a7 7 0 1 1 14 0c0 4.5-7 12-7 12Z" /><circle cx="12" cy="9" r="2.2" /></>,
    clock:    <><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3 2" /></>,
    doc:      <><path d="M7 3h8l4 4v14H7z" /><path d="M15 3v4h4M10 12h6M10 16h6M10 8h2" /></>,
    dot:      <circle cx="12" cy="12" r="3" fill={stroke} />,
    close:    <path d="M6 6l12 12M18 6 6 18" />,
    plus:     <path d="M12 5v14M5 12h14" />,
    check:    <path d="m5 12 4 4L19 7" />,
    star:     <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" />,
    book:     <><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" /><path d="M4 5v16" /></>,
    globe:    <><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17M12 3.5c3 3 3 14 0 17M12 3.5c-3 3-3 14 0 17" /></>,
    rss:      <><path d="M5 19a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" fill={stroke} stroke="none" /><path d="M5 12a7 7 0 0 1 7 7M5 5a14 14 0 0 1 14 14" /></>,
    filter:   <path d="M4 5h16l-6 8v5l-4 2v-7z" />,
    menu:     <path d="M4 7h16M4 12h16M4 17h16" />,
    bookmark: <path d="M7 4h10v17l-5-3-5 3z" />,
    mail:     <><rect x="3" y="5" width="18" height="14" rx="1.5" /><path d="m3 7 9 6 9-6" /></>,
    phone:    <path d="M5 4h3l2 5-3 2a14 14 0 0 0 6 6l2-3 5 2v3a2 2 0 0 1-2 2A17 17 0 0 1 3 6a2 2 0 0 1 2-2z" />,
  };
  return <svg {...common}>{paths[name]}</svg>;
};

/* ─── Sparkline — tiny trend marker ─────────────────────────────── */
export const Sparkline: React.FC<{ data: number[]; color?: string; w?: number; h?: number }> = ({
  data,
  color = "currentColor",
  w = 80,
  h = 24,
}) => {
  const max = Math.max(...data), min = Math.min(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 2) - 1}`)
    .join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.2" />
    </svg>
  );
};
